import type { GridSelection } from "@glideapps/glide-data-grid";
import { type RefObject, useEffect, useRef } from "react";
import {
  getFormulaFillShortcutRequest,
  isFormulaSourceRange,
  matchesFormulaFillShortcut,
  shouldHandleFormulaFillShortcut,
  type CellLookup,
  type FormulaFillRequest,
  type FormulaFillShortcutAction,
} from "./formula-fill";

const FORMULA_FILL_LABELS: Record<FormulaFillShortcutAction, string> = {
  down: "Fill down",
  right: "Fill right",
  range: "Fill range",
};

interface UseFormulaFillInteractionsParams {
  enabled: boolean;
  getCellRecord: CellLookup | undefined;
  gridSelection: GridSelection;
  gridWrapperRef: RefObject<HTMLDivElement>;
  onFill: (request: FormulaFillRequest, label: string) => void;
}

export const useFormulaFillInteractions = ({
  enabled,
  getCellRecord,
  gridSelection,
  gridWrapperRef,
  onFill,
}: UseFormulaFillInteractionsParams) => {
  const fillCancelledRef = useRef(false);
  const onFillRef = useRef(onFill);
  onFillRef.current = onFill;

  // Glide previews fill destinations without writing until pointer-up. Mark an
  // Escape pressed during that gesture before Glide clears the selection, so a
  // stale pointer-up callback cannot commit after cancellation.
  useEffect(() => {
    const handleFillCancellation = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const activeElement = document.activeElement;
      if (
        activeElement instanceof Node &&
        (gridWrapperRef.current?.contains(activeElement) ?? false)
      ) {
        fillCancelledRef.current = true;
      }
    };

    // Escape can also be pressed while the grid is idle. Starting a new pointer
    // gesture establishes a fresh fill attempt so stale cancellation state does
    // not discard it.
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        (gridWrapperRef.current?.contains(target) ?? false)
      ) {
        fillCancelledRef.current = false;
      }
    };

    document.addEventListener("keydown", handleFillCancellation, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("keydown", handleFillCancellation, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [gridWrapperRef]);

  // Formula fill shortcuts are scoped to the grid. The formula bar and Glide's
  // text overlay retain their native shortcuts while they have focus.
  useEffect(() => {
    const handleFormulaFillShortcut = (event: KeyboardEvent) => {
      if (!enabled || !getCellRecord || event.defaultPrevented) return;

      const action = matchesFormulaFillShortcut(event);
      if (!action) return;

      const request = getFormulaFillShortcutRequest(action, gridSelection);
      if (!request) return;

      const activeElement = document.activeElement;
      const textEditorHasFocus =
        activeElement instanceof HTMLElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable);
      const viewerHasFocus =
        activeElement instanceof Node &&
        (gridWrapperRef.current?.contains(activeElement) ?? false);
      const sourceIsFormula = isFormulaSourceRange(
        request.patternSource,
        getCellRecord,
      );

      if (
        !shouldHandleFormulaFillShortcut({
          viewerHasFocus,
          textEditorHasFocus,
          sourceIsFormula,
        })
      ) {
        return;
      }

      event.preventDefault();
      onFillRef.current(request, FORMULA_FILL_LABELS[action]);
    };

    document.addEventListener("keydown", handleFormulaFillShortcut);
    return () =>
      document.removeEventListener("keydown", handleFormulaFillShortcut);
  }, [enabled, getCellRecord, gridSelection, gridWrapperRef]);

  return {
    consumeFillCancellation: () => {
      const wasCancelled = fillCancelledRef.current;
      fillCancelledRef.current = false;
      return wasCancelled;
    },
  };
};
